import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import type { ShippingAddressFragment } from '@graphql/aliases';
import { getLocalStorage } from '@shared/core';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useDisclosure } from '@withjoy/joykit';

import type { LineItem, HistoryItem, ProductListV2 } from '../ShoppingCart.types';
import { CartCheckoutSteps, ShippingAddressOptions, ShippingMethods } from '../ShoppingCart.types';
import { CartSections } from '../ShoppingCart.constants';
import type { DetailsFormFields } from '../ShoppingCartInnerV2/steps/DetailsStep/types';
import type { RegistryOrderFragment } from '@graphql/fragments/registry.graphql.generated';
import type { RegistryLineItemInput, ShippingAddressInput } from '@graphql/generated-base';
import { useGetEventRegistriesAndOrdersQuery } from '../../../../guest/routes/GuestRegistry/components/GuestShopping/GuestShopping.graphql.generated';
import { useGetCalculateRegistryPurchaseInfoQuery } from '@graphql/queries/registry.graphql.generated';
import {
  STATE_INITIAL,
  NATIONAL_COUNTRY_CODE,
  getInitialState,
  getUpdatedStateByUpdateItem,
  getUpdatedStateByRemoveItem,
  getUpdatedStateByAddItem,
  getStillNeeded,
  DERIVED_STATE_INITIAL,
  NATIONAL_COUNTRY,
  getInitialHistoryState,
  getMarkedAsVisited,
  getBuyNowLineItem,
  getProductsStillNeeded,
  isLineItemKnownUnavailable,
  HISTORY_STATE_INITIAL,
} from './ShoppingCart.utils';
import { noop } from 'lodash-es';
import { deriveAggregateCheckoutCodesResult } from '@apps/registry/common/utils/checkoutMechanisms';
import type { AdditionalCostLine } from '@apps/registry/common/utils/additionalCostLines';
import { computeRegistryPurchaseInfoCostLines } from '@apps/registry/common/utils/additionalCostLines';
import type { GiftWrapOfferState } from '@apps/registry/guest/components/CheckoutDialog/components/GiftWrapOffer/GiftWrapOffer';
import { prepareSampleECardPayload, useGiftWrapECardPrice } from '@shared/components/GiftWrap';
import { GiftWrapType } from '@apps/registry/guest/GuestRegistry.telemetry';

const localStorage = getLocalStorage();

export const SHOPPING_CART_INITIAL: ProviderProps = {
  state: { ...STATE_INITIAL, ...HISTORY_STATE_INITIAL, ...DERIVED_STATE_INITIAL },
  mutators: {
    // Storage
    addItem: noop,
    removeItem: noop,
    updateItem: noop,
    clearState: noop,

    // History State
    markAsVisited: noop,

    // Section & Step
    updateCurrentSection: noop,
    updateCurrentCheckoutStep: noop,

    // Shipping Step
    updateShippingOption: noop,
    updateShippingAddress: noop,
    updateShippingMethod: noop,

    // Details Step
    updateDetails: noop,
    updatePurchaseInfoGiftWrap: noop,

    // General Cart
    openShoppingCart: noop,
    closeShoppingCart: noop,
    buyNowFromShoppingCart: noop,
    syncItems: noop,
  },
};

export type HistoryState = {
  historyItemList: Array<HistoryItem>;
};
export type State = {
  itemList: Array<LineItem>;
  lastUpdated: number | null;
  hadOnlyStillNeededItems: boolean;
};

export type DerivedState = {
  // Step & Section
  checkoutStepsFlow: CartCheckoutSteps[];
  currentSection: CartSections;
  currentCheckoutStep: CartCheckoutSteps;

  // Shipping Step
  shippingMethod: ShippingMethods;
  selectedShippingOption: ShippingAddressOptions;
  shippingAddress?: Maybe<ShippingAddressFragment>;
  coupleAddress?: Maybe<ShippingAddressFragment>;
  supportsShipToMe: boolean;
  customShippingAddress?: Maybe<ShippingAddressFragment>;

  // Shipping cost is treated separately from other 'additional cost' lines
  shippingFee?: Maybe<string>;
  shippingFeeInMajorUnits: number;
  surchargeFee: { totalInMajorUnits: number; itemsCount: number } | null | undefined;
  additionalCostLines: AdditionalCostLine[]; // excludes delivery and surcharge
  additionalCostInMajorUnits: number;
  taxesTotalInMajorUnits?: number;

  // Details Step
  details: DetailsFormFields;

  // General Cart
  isShoppingCartOpen: boolean;
  itemListCount: number;
  cartItemCount: number;
  expressCheckoutEnabled?: boolean;
  isSyncing: boolean;
  lastSyncAt: Date | null;
};

export type GeneralState = State & HistoryState & DerivedState;
export type Mutators = {
  // State
  addItem: (data: LineItem, maxQuantity: number) => void;
  removeItem: (registryItemId: string) => void;
  updateItem: (data: LineItem) => void;
  syncItems: () => void;
  clearState: () => void;

  // History State
  markAsVisited: (reservedOrderList: RegistryOrderFragment[], productList: ProductListV2) => void;

  // Section & Step
  updateCurrentSection: (section: CartSections) => void;
  updateCurrentCheckoutStep: (step: CartCheckoutSteps) => void;

  // Shipping Step
  updateShippingAddress: (address: ShippingAddressFragment) => void;
  updateShippingOption: (option: ShippingAddressOptions) => void;
  updateShippingMethod: (method: ShippingMethods) => void;

  // Details Step
  updateDetails: (details: DetailsFormFields) => void;
  updatePurchaseInfoGiftWrap: (giftWrap: GiftWrapOfferState) => void;

  // General Cart
  closeShoppingCart: () => void;
  openShoppingCart: () => void;
  buyNowFromShoppingCart: (data: LineItem, maxQuantity: number) => void;
};

export interface ProviderProps {
  state: GeneralState;
  mutators: Mutators;
}

export const ShoppingCartContext = createContext(SHOPPING_CART_INITIAL);

type ShoppingCartProviderProps = {
  coupleAddress?: Maybe<ShippingAddressFragment>;
  checkoutStepsFlow: CartCheckoutSteps[];
  eventHandle: string;
  eventId?: string;
  expressCheckoutEnabled?: boolean;
};

export const ShoppingCartProvider: React.FC<ShoppingCartProviderProps> = ({ children, coupleAddress, eventHandle, eventId, checkoutStepsFlow, expressCheckoutEnabled }) => {
  const shoppingCartKey = `shoppingCart_${eventId}`;
  const shoppingCartHistoryKey = `shoppingCartHistory_${eventId}`;
  const { Provider } = ShoppingCartContext;

  // Section & step state
  const [currentSection, setCurrentSection] = useState(CartSections.CART);
  const [currentCheckoutStep, setCurrentCheckoutStep] = useState(CartCheckoutSteps.Cart);

  // Shipping Step
  // If guest chooses to ship to themselves or to another US address
  const [customShippingAddress, setCustomShippingAddress] = useState<ShippingAddressFragment | null>({
    name: '',
    address1: '',
    city: '',
    countryCode: NATIONAL_COUNTRY_CODE,
    country: NATIONAL_COUNTRY,
    state: '',
    validated: false,
  });
  const [selectedShippingOption, setSelectedShippingOption] = useState<ShippingAddressOptions>(
    coupleAddress ? ShippingAddressOptions.CoupleAddress : ShippingAddressOptions.AnotherAddress,
  );
  const [shippingMethod, setShippingMethod] = useState<ShippingMethods>(ShippingMethods.FreeByJoy);
  // selectedShippingAddress variable stores wether the couple address or form values from address form.
  // So if user filled the form, then clicked "ship to couple" and then clicked "shipped to me" again - he will see the pre-filled form
  const shippingAddress = useMemo(() => {
    const defaultAddress = { ...coupleAddress!, __typename: undefined };
    return selectedShippingOption === ShippingAddressOptions.CoupleAddress ? defaultAddress : customShippingAddress;
  }, [coupleAddress, customShippingAddress, selectedShippingOption]);
  // Details Step
  const [details, setDetails] = useState<DetailsFormFields>({
    name: '',
    message: '',
    email: '',
  });
  const [purchaseInfoGiftWrap, setPurchaseInfoGiftWrap] = useState<GiftWrapOfferState | null>(null);
  useEffect(() => {
    if (currentCheckoutStep !== CartCheckoutSteps.Message) {
      setPurchaseInfoGiftWrap(null);
    }
  }, [currentCheckoutStep]);
  const giftWrapPrice = useGiftWrapECardPrice();
  const calculatePurchaseInfoECard = useMemo(
    () => (purchaseInfoGiftWrap?.wrapOption === 'eCard' ? prepareSampleECardPayload(giftWrapPrice, GiftWrapType.CART) : undefined),
    [giftWrapPrice, purchaseInfoGiftWrap?.wrapOption],
  );
  const isMobileOrTablet = useIsMobileOrTablet();

  // General cart
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [buyNowItem, setBuyNowItem] = useState<LineItem | null>(null);

  const updateCurrentCheckoutStep = useCallback((step: CartCheckoutSteps) => {
    if (step === CartCheckoutSteps.Cart) {
      setBuyNowItem(null);
    }
    setCurrentCheckoutStep(step);
  }, []);

  const closeShoppingCart = () => {
    setCurrentSection(CartSections.CART);
    updateCurrentCheckoutStep(CartCheckoutSteps.Cart);
    onClose();
  };

  // History state
  const [historyState, setHistoryState] = useState<HistoryState>(getInitialHistoryState(shoppingCartHistoryKey));
  const markAsVisited = (reservedOrderList: RegistryOrderFragment[], productList: ProductListV2) => {
    setHistoryState((prevState: HistoryState) => getMarkedAsVisited(prevState, reservedOrderList, productList));
  };

  // State
  const [state, setState] = useState<State>(getInitialState(shoppingCartKey));
  const { data: registriesAndOrders, refetch: getEventRegistriesAndOrders } = useGetEventRegistriesAndOrdersQuery({
    batchMode: 'fast',
    fetchPolicy: 'no-cache',
    variables: { eventHandle },
  });

  const buyNowItemList = useMemo(() => (buyNowItem ? [buyNowItem] : null), [buyNowItem]);
  const checkoutItemList = buyNowItemList ?? state.itemList;

  const hadOnlyStillNeededItems = useMemo(() => {
    if (!buyNowItem) {
      return state.hadOnlyStillNeededItems;
    }
    const productsStillNeeded = registriesAndOrders && getProductsStillNeeded(registriesAndOrders);
    return !productsStillNeeded || !isLineItemKnownUnavailable(buyNowItem, productsStillNeeded);
  }, [buyNowItem, registriesAndOrders, state.hadOnlyStillNeededItems]);

  const lineItems: RegistryLineItemInput[] = checkoutItemList.map(item => ({ quantity: item.quantity, registryItemId: item.registryItemId }));
  const shippingAddressInput: ShippingAddressInput | undefined = shippingAddress ? (({ hash, ...rest }) => rest)(shippingAddress) : undefined;

  const getCalculateRegistryPurchaseInfoQuery = useGetCalculateRegistryPurchaseInfoQuery({
    variables: {
      payload: {
        eventId: eventId!,
        eCard: calculatePurchaseInfoECard ?? details.eCard ?? undefined,
        address: shippingAddressInput,
        lineItems,
      },
    },
    skip: !shippingAddressInput || lineItems.length === 0 || !eventId || !isOpen,
    batchMode: 'fast',
  });
  const calculateRegistryPurchaseInfo = getCalculateRegistryPurchaseInfoQuery.data?.calculateRegistryPurchaseInfo;
  const costInfo = useMemo(() => computeRegistryPurchaseInfoCostLines(calculateRegistryPurchaseInfo), [calculateRegistryPurchaseInfo]);

  const { isLowInventory } = deriveAggregateCheckoutCodesResult([calculateRegistryPurchaseInfo]) ?? {};

  const additionalCostLines = costInfo?.additionalCostLines ?? [];
  const additionalCostInMajorUnits = (costInfo?.totalAdditionalCost?.valueInMinorUnits ?? 0) / 100;
  const taxesTotalInMajorUnits =
    currentCheckoutStep === CartCheckoutSteps.Message && purchaseInfoGiftWrap?.isReady && calculateRegistryPurchaseInfo?.taxesTotal
      ? calculateRegistryPurchaseInfo.taxesTotal.valueInMinorUnits / 100
      : undefined;

  const addItem = (data: LineItem, maxQuantity: number) => {
    setBuyNowItem(null);
    setState((prevState: State) => getUpdatedStateByAddItem(prevState, data, maxQuantity));

    if (!isMobileOrTablet) {
      onOpen();
    }
    setCurrentSection(CartSections.CART);
  };

  const removeItem = (registryItemId: string) => {
    setState((prevState: State) => getUpdatedStateByRemoveItem(prevState, registryItemId));
  };

  const updateItem = (data: LineItem) => {
    setState((prevState: State) => getUpdatedStateByUpdateItem(prevState, data));
  };

  const clearState = () => {
    setState(STATE_INITIAL);
  };

  // Cleanup remove already purchased/reserved products
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [isSyncing, setSyncing] = useState<boolean>(true);

  useEffect(() => {
    if (registriesAndOrders) {
      setState((prevState: State) => getStillNeeded(prevState, registriesAndOrders));
      setLastSyncAt(new Date());
      setSyncing(false);
    }
  }, [registriesAndOrders, setState, setLastSyncAt, setSyncing]);

  const syncItems = useCallback(async () => {
    setSyncing(true);
    try {
      await getEventRegistriesAndOrders();
      setLastSyncAt(new Date());
    } finally {
      setSyncing(false);
    }
  }, [getEventRegistriesAndOrders]);

  // Update local storage
  useEffect(() => {
    if (!eventId) {
      return;
    }
    localStorage.setItem(shoppingCartKey, JSON.stringify(state));
  }, [state, shoppingCartKey, eventId]);

  // Update history local storage
  useEffect(() => {
    if (!eventId) {
      return;
    }
    localStorage.setItem(shoppingCartHistoryKey, JSON.stringify(historyState));
  }, [historyState, shoppingCartHistoryKey, eventId]);

  const itemListCount = useMemo(() => checkoutItemList.reduce((total, product) => total + product.quantity, 0), [checkoutItemList]);
  const cartItemCount = useMemo(() => state.itemList.reduce((total, product) => total + product.quantity, 0), [state.itemList]);
  const buyNowFromShoppingCart = (data: LineItem, maxQuantity: number) => {
    setBuyNowItem(getBuyNowLineItem(data, maxQuantity));
    setCurrentSection(CartSections.CART);
    //open the shopping cart properly so data is propagated correctly - specially for mobile.
    onOpen();
    //set the shipping address checkout step.
    updateCurrentCheckoutStep(CartCheckoutSteps.Shipping);
  };
  // currently, we say nothing about orderCharges
  const shippingFee = costInfo?.deliveryTotalValue.floatingPointDecimalString;
  const shippingFeeInMajorUnits = (costInfo?.deliveryTotalValue.valueInMinorUnits ?? 0) / 100;
  const surchargeFee = !costInfo
    ? undefined // loading — distinguish from null (loaded, no surcharge)
    : costInfo.bulkSurchargeTotalValue != null
      ? { totalInMajorUnits: costInfo.bulkSurchargeTotalValue.valueInMinorUnits / 100, itemsCount: costInfo.bulkSurchargeShippingLines.length }
      : null;
  const supportsShipToMe = !isLowInventory;

  return (
    <Provider
      value={{
        state: {
          // State
          checkoutStepsFlow,
          itemList: checkoutItemList,
          itemListCount,
          cartItemCount,
          lastUpdated: state.lastUpdated,
          hadOnlyStillNeededItems,

          // History state
          historyItemList: historyState.historyItemList,

          // Section & Step
          currentSection,
          currentCheckoutStep,

          // General Cart
          isShoppingCartOpen: isOpen,
          expressCheckoutEnabled,
          isSyncing,
          lastSyncAt,

          // Shipping Step
          coupleAddress,
          supportsShipToMe,
          customShippingAddress,
          shippingAddress,
          shippingMethod,
          selectedShippingOption,
          shippingFee,
          shippingFeeInMajorUnits,
          surchargeFee,
          additionalCostLines,
          additionalCostInMajorUnits,
          taxesTotalInMajorUnits,

          // Details step
          details,
        },
        mutators: {
          // State
          addItem,
          removeItem,
          updateItem,
          clearState,
          syncItems,

          // History State
          markAsVisited,

          // Section & Step
          updateCurrentSection: setCurrentSection,
          updateCurrentCheckoutStep,

          // General Cart
          closeShoppingCart,
          openShoppingCart: onOpen,
          buyNowFromShoppingCart,

          // Shipping Step
          updateShippingAddress: setCustomShippingAddress,
          updateShippingOption: setSelectedShippingOption,
          updateShippingMethod: setShippingMethod,

          // Details step
          updateDetails: setDetails,
          updatePurchaseInfoGiftWrap: setPurchaseInfoGiftWrap,
        },
      }}
    >
      {children}
    </Provider>
  );
};
